export const MODEL_VIEWER_PRESENTATION_SETUP_IDS = ['neutral', 'studio'] as const;

export type ModelViewerPresentationSetup = typeof MODEL_VIEWER_PRESENTATION_SETUP_IDS[number];

export interface ModelViewerPresentationSetupConfig {
    cameraDistanceScale: number
    cameraFov: number
    framingMargin: false | number
}

// Public payload limits live beside their defaults so the standalone sliders,
// parser, and scene always agree on the supported photography range.
export const MODEL_VIEWER_CAMERA_FOV_MIN = 20;
export const MODEL_VIEWER_CAMERA_FOV_MAX = 55;
export const MODEL_VIEWER_CYCLORAMA_BOUNCE_STRENGTH_MIN = 0;
export const MODEL_VIEWER_CYCLORAMA_BOUNCE_STRENGTH_MAX = 1;
export const MODEL_VIEWER_CYCLORAMA_GLOSSINESS_MIN = 0;
export const MODEL_VIEWER_CYCLORAMA_GLOSSINESS_MAX = 1;
export const MODEL_VIEWER_CYCLORAMA_REFLECTION_STRENGTH_MIN = 0;
export const MODEL_VIEWER_CYCLORAMA_REFLECTION_STRENGTH_MAX = 1;
export const MODEL_VIEWER_STUDIO_DIRECT_LIGHT_INTENSITY_MIN = 0;
export const MODEL_VIEWER_STUDIO_DIRECT_LIGHT_INTENSITY_MAX = 2;
export const MODEL_VIEWER_STUDIO_AMBIENT_LIGHT_INTENSITY_MIN = 0;
export const MODEL_VIEWER_STUDIO_AMBIENT_LIGHT_INTENSITY_MAX = 2;
export const DEFAULT_MODEL_VIEWER_CAMERA_FOV = 35;
export const DEFAULT_MODEL_VIEWER_CYCLORAMA_BOUNCE_STRENGTH = 0.45;
export const DEFAULT_MODEL_VIEWER_CYCLORAMA_COLOR = '#f3f3f3';
export const DEFAULT_MODEL_VIEWER_CYCLORAMA_GLOSSINESS = 0;
export const DEFAULT_MODEL_VIEWER_CYCLORAMA_REFLECTION_STRENGTH = 0.5;
export const DEFAULT_MODEL_VIEWER_STUDIO_DIRECT_LIGHT_INTENSITY = 1;
export const DEFAULT_MODEL_VIEWER_STUDIO_AMBIENT_LIGHT_INTENSITY = 1;

export const DEFAULT_MODEL_VIEWER_PRESENTATION_SETUP:ModelViewerPresentationSetup = 'neutral';

// Runtime guards are used at the JSON/browser-event boundary. Keeping them
// here avoids duplicating range knowledge in the request parser.
export const isModelViewerCameraFov = (value: unknown):value is number => (
    typeof value === 'number'
    && Number.isFinite(value)
    && value >= MODEL_VIEWER_CAMERA_FOV_MIN
    && value <= MODEL_VIEWER_CAMERA_FOV_MAX
);

export const isModelViewerCycloramaColor = (value: unknown):value is string => (
    typeof value === 'string' && /^#[0-9a-fA-F]{6}$/.test(value)
);

export const isModelViewerCycloramaBounceStrength = (
    value: unknown,
):value is number => (
    typeof value === 'number'
    && Number.isFinite(value)
    && value >= MODEL_VIEWER_CYCLORAMA_BOUNCE_STRENGTH_MIN
    && value <= MODEL_VIEWER_CYCLORAMA_BOUNCE_STRENGTH_MAX
);

export const isModelViewerCycloramaGlossiness = (
    value: unknown,
):value is number => (
    typeof value === 'number'
    && Number.isFinite(value)
    && value >= MODEL_VIEWER_CYCLORAMA_GLOSSINESS_MIN
    && value <= MODEL_VIEWER_CYCLORAMA_GLOSSINESS_MAX
);

export const isModelViewerCycloramaReflectionStrength = (
    value: unknown,
):value is number => (
    typeof value === 'number'
    && Number.isFinite(value)
    && value >= MODEL_VIEWER_CYCLORAMA_REFLECTION_STRENGTH_MIN
    && value <= MODEL_VIEWER_CYCLORAMA_REFLECTION_STRENGTH_MAX
);

export const isModelViewerStudioDirectLightIntensity = (
    value: unknown,
):value is number => (
    typeof value === 'number'
    && Number.isFinite(value)
    && value >= MODEL_VIEWER_STUDIO_DIRECT_LIGHT_INTENSITY_MIN
    && value <= MODEL_VIEWER_STUDIO_DIRECT_LIGHT_INTENSITY_MAX
);

export const isModelViewerStudioAmbientLightIntensity = (
    value: unknown,
):value is number => (
    typeof value === 'number'
    && Number.isFinite(value)
    && value >= MODEL_VIEWER_STUDIO_AMBIENT_LIGHT_INTENSITY_MIN
    && value <= MODEL_VIEWER_STUDIO_AMBIENT_LIGHT_INTENSITY_MAX
);

export const isModelViewerPresentationSetup = (
    value: unknown,
):value is ModelViewerPresentationSetup => (
    MODEL_VIEWER_PRESENTATION_SETUP_IDS.includes(value as ModelViewerPresentationSetup)
);

export const MODEL_VIEWER_PRESENTATION_SETUPS:Record<
    ModelViewerPresentationSetup,
    ModelViewerPresentationSetupConfig
> = {
    // The setup map is also the extension point for setup-specific framing.
    // Values currently match so switching presentation does not reframe a
    // product unless a pedestal changes its measured height.
    neutral: {
        cameraDistanceScale: 1,
        cameraFov: DEFAULT_MODEL_VIEWER_CAMERA_FOV,
        framingMargin: false,
    },
    studio: {
        cameraDistanceScale: 1,
        cameraFov: DEFAULT_MODEL_VIEWER_CAMERA_FOV,
        framingMargin: false,
    },
};
